import React, { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination } from "@mui/material";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import CustomButton from "components/common/Button";
import CustomChip from "components/common/Chip";
import SectionLoader from "components/common/SectionLoader";
import { getReferralData, ReferralRockMember } from "services/referral";
import { ReferralStats, ReferralItem } from "./types";
import "./MyReferrals.scss";

interface MyReferralsProps {
  memberData: ReferralRockMember | null;
}

function MyReferrals({ memberData }: MyReferralsProps) {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [referrals, setReferrals] = useState<ReferralItem[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (memberData?.id) {
      fetchReferralData();
    }
  }, [page, rowsPerPage, memberData]);

  const fetchReferralData = async () => {
    if (!memberData?.id) return;

    setLoading(true);
    const response = await getReferralData(page + 1, rowsPerPage, memberData.id);
    if (response.ok) {
      setStats(response.payload.stats);
      setReferrals(response.payload.referrals);
      setTotal(response.payload.total);
    }
    setLoading(false);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hasNoReferrals = total === 0 && !loading;

  if (loading && !stats) {
    return (
      <div className="my-referrals-tab flex justify-center items-center">
        <SectionLoader size={40} />
      </div>
    );
  }

  return (
    <div className="my-referrals-tab">
      {hasNoReferrals ? (
        <div className="empty-state-container">
          <div className="empty-state-icon">
            <SentimentDissatisfiedIcon className="sentiment-icon" />
          </div>
          <div className="empty-state-content">
            <h3 className="empty-state-title">You haven&apos;t referred anyone yet</h3>
            <p className="empty-state-description">
              Invite other businesses to Edge and earn a $2,000 reward for each successful referral.
            </p>
          </div>
          <CustomButton
            label="Refer a Business"
            onClick={() => {
              const tabs = document.querySelector("[role='tablist']");
              const firstTab = tabs?.querySelector("[role='tab']") as HTMLElement;
              firstTab?.click();
            }}
            variant="contained"
            color="primary"
            size="medium"
          />
        </div>
      ) : (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-label">Referral link opened</div>
              <div className="stat-value">{stats?.referralLinkUniqueVisitors || 0}</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Referrals approved</div>
              <div className="stat-value">{stats?.referralsApproved || 0}</div>
              <div className="stat-subtext">{stats?.referralsApprovedTotal || 0} in total</div>
            </div>

            <div className="stat-card">
              <div className="stat-label">Total earned</div>
              <div className="stat-value">{stats?.totalEarned || "$0"}</div>
              <div className="stat-subtext">{stats?.pendingEarnings || "$0"} pending</div>
            </div>
          </div>

          <div className="referrals-table-container">
            <TableContainer>
              <Table className="referrals-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Referral</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Earnings</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <SectionLoader size={40} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    referrals.map((referral) => (
                      <TableRow key={referral.id}>
                        <TableCell>{referral.email}</TableCell>
                        <TableCell>{referral.companyName}</TableCell>
                        <TableCell>{referral.earnings || "-"}</TableCell>
                        <TableCell>
                          <CustomChip
                            label={referral.status}
                            variant="filled"
                            color={referral.statusType}
                            size="small"
                            labelClass=""
                            onClick={null}
                            onDelete={null}
                          />
                        </TableCell>
                        <TableCell>{referral.lastUpdated}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {total > rowsPerPage && (
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default MyReferrals;
